import * as moment from 'moment';
import { useState } from 'react';
import { IRelativePerformance } from '@nkr/models/center-performance';
import { Glyphicon, Label, ListGroup, ListGroupItem, OverlayTrigger, Panel } from 'react-bootstrap';
import { TableWrapper } from './styles';
import * as RTO from './relative-performance-overlays';

interface IProps {
    data: IRelativePerformance,
    memberCenterCount: number,
    centerId: number,
    isSuperUser: boolean,
    loading: boolean
}

const RelativePerformance = (props: IProps) => {
    const getRankText = (rank: number) => {
        return <>{rank}</>;
    };

    const renderCompositeScore = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let compositeScore = props.data.compositeScore;

        return (
            <tr>
                <td style={{ width: 30 }}>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/reports/my-center/composite-score`} target='_blank'>
                        Composite Score
                    </a>
                </td>
                <td>
                    <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                        <RTO.CompositeScoreOverlay />
                    }>
                        <div style={{ cursor: 'pointer' }}>
                            {['Green'].indexOf(compositeScore.category) >= 0 && (
                                <>{compositeScore.compositeScore}</>
                            )}

                            {['N/A'].indexOf(compositeScore.category) >= 0 && (
                                <Label>{compositeScore.category}</Label>
                            )}
                        </div>
                    </OverlayTrigger>
                </td>
                <td>
                    {['Green'].indexOf(compositeScore.category) >= 0 && (
                        <>{getRankText(compositeScore.rank)}</>
                    )}

                    {['N/A'].indexOf(compositeScore.category) >= 0 && (
                        <Label>{compositeScore.category}</Label>
                    )}
                </td>
            </tr>
        );
    };

    const render30DayPreselect = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let preselectsAveragePercentReviewedThisMonth = props.data.preselectsAveragePercentReviewedThisMonth;

        return (
            <tr>
                <td style={{ width: 30 }}>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/reports/my-center/preselect-details`} target='_blank'>
                        30 Day Preselect %
                    </a>
                </td>
                <td>
                    <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                        <RTO.ThirtyDayPreselectOverlay />
                    }>
                        <div style={{ cursor: 'pointer' }}>
                            {['Green', 'Red'].indexOf(preselectsAveragePercentReviewedThisMonth.category) >= 0 && (
                                <>{preselectsAveragePercentReviewedThisMonth.average}%</>
                            )}

                            {['LV', 'NV'].indexOf(preselectsAveragePercentReviewedThisMonth.category) >= 0 && (
                                <Label>{preselectsAveragePercentReviewedThisMonth.category}</Label>
                            )}
                        </div>
                    </OverlayTrigger>
                </td>
                <td>
                    {['Green', 'Red'].indexOf(preselectsAveragePercentReviewedThisMonth.category) >= 0 && (
                        <>{getRankText(preselectsAveragePercentReviewedThisMonth.rank)}</>
                    )}

                    {['LV', 'NV'].indexOf(preselectsAveragePercentReviewedThisMonth.category) >= 0 && (
                        <Label>{preselectsAveragePercentReviewedThisMonth.category}</Label>
                    )}
                </td>
            </tr>
        );
    };

    const renderSurgeryDateDecline = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let surgeryDateDecline = props.data.surgeryDateDecline;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/reports/my-center/surgical-date-declines?last12=1`} target='_blank'>
                        Surgery Date Declines - Last 12 Months
                    </a>
                </td>
                <td>
                    <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                        <RTO.SurgeryDateDeclineOverlay surgeryDateDecline={surgeryDateDecline} />
                    }>
                        <div style={{ cursor: 'pointer' }}>
                            {['Green', 'Red'].indexOf(surgeryDateDecline.category) >= 0 && (
                                <>{(+surgeryDateDecline.declineRate).toFixed(1)}%</>
                            )}

                            {['LV', 'NV'].indexOf(surgeryDateDecline.category) >= 0 && (
                                <Label>{surgeryDateDecline.category}</Label>
                            )}
                        </div>
                    </OverlayTrigger>
                </td>
                <td>
                    <div>
                        {['Green', 'Red'].indexOf(surgeryDateDecline.category) >= 0 && (
                            <>{getRankText(surgeryDateDecline.rank)}</>
                        )}

                        {['LV', 'NV'].indexOf(surgeryDateDecline.category) >= 0 && (
                            <Label>{surgeryDateDecline.category}</Label>
                        )}
                    </div>
                </td>
            </tr>
        );
    };

    const renderSwapFailureRate = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let swapFailureRate = props.data.swapFailureRate;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/reports/my-center/rccap`} target='_blank'>
                        Swap Failure Rate - Last 12 Months
                    </a>
                </td>
                <td>
                    <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                        <RTO.SwapFailureRateOverlay swapFailureRate={swapFailureRate} />
                    }>
                        <div style={{ cursor: 'pointer' }}>
                            {['Green', 'Red'].indexOf(swapFailureRate.category) >= 0 && (
                                <>{Math.round(swapFailureRate.failureRate)}%</>
                            )}

                            {['LV', 'NV'].indexOf(swapFailureRate.category) >= 0 && (
                                <Label>{swapFailureRate.category}</Label>
                            )}
                        </div>
                    </OverlayTrigger>
                </td>
                <td>
                    <div>
                        {['Green', 'Red'].indexOf(swapFailureRate.category) >= 0 && (
                            <>{getRankText(swapFailureRate.rank)}</>
                        )}

                        {['LV', 'NV'].indexOf(swapFailureRate.category) >= 0 && (
                            <Label>{swapFailureRate.category}</Label>
                        )}
                    </div>
                </td>
            </tr>
        );
    };

    const renderChainEndDeclineRatio = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let chainEndDeclineRatio = props.data.chainEndDeclineRatio;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    {<a href={`/center/${props.centerId}/reports/my-center/waitlist-audit-report`} target="_blank">
                        Waitlist Offer Decline Ratio - Last 6 Months
                    </a>}
                </td>
                <td>
                    {chainEndDeclineRatio.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.ChainEndDeclineRatioOverlay chainEndDeclineRatio={chainEndDeclineRatio} />
                        }>
                            <div style={{ cursor: 'pointer' }}>
                                {chainEndDeclineRatio.declineRatio}%
                            </div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {chainEndDeclineRatio.category != 'NV' ?
                        <div>
                            {getRankText(chainEndDeclineRatio.rank)}
                        </div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderNkrDonorReferralMedianContactTime = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let nkrDonorReferralMedianContactTime = props.data.nkrDonorReferralMedianContactTime;

        let value = '';

        if (nkrDonorReferralMedianContactTime.contactTimeDaysMedian > 1) {
            value = nkrDonorReferralMedianContactTime.contactTimeDaysMedian + ' days';
        }

        if (nkrDonorReferralMedianContactTime.contactTimeDaysMedian == 1) {
            value = '1 day';
        }

        if (nkrDonorReferralMedianContactTime.contactTimeDaysMedian < 1) {
            value = '< day';
        }

        let currentYear = moment().month() < 3 ? moment().year() - 1 : moment().year();

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    {<a href={`/center/${props.centerId}/reports/dash-board/compare-conversion?sites[]=nkr&year=${currentYear}&min=0&organType=1`} target="_blank">
                        NKR Donor Referral Median Contact Time - Last 12 Months
                    </a>}
                </td>
                <td>
                    {nkrDonorReferralMedianContactTime.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.NkrDonorReferralMedianContactTimeOverlay nkrDonorReferralMedianContactTime={nkrDonorReferralMedianContactTime} />
                        }>
                            <div style={{ cursor: 'pointer' }}>
                                {value}
                            </div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {nkrDonorReferralMedianContactTime.category != 'NV' ?
                        <div>
                            {getRankText(nkrDonorReferralMedianContactTime.rank)}
                        </div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderNkrDonorReferralConversionRate = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let nkrDonorReferralConversionRate = props.data.nkrDonorReferralConversionRate;
        let referralYear = moment().month() < 3 ? (moment().year() - 1) : moment().year();
        let startDate = moment('01/01/' + referralYear, 'MM/DD/YYYY').format('YYYY-MM-DD');
        let endDate = moment('12/31/' + referralYear, 'MM/DD/YYYY').format('YYYY-MM-DD');

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/donors/donor-intake/search?id=0&adv=1&nkr=1&dash=0&ms=0&sd=${startDate}&ed=${endDate}&d=-1&s=2&stat=-1&organ=0`} target='_blank'>
                        NKR Donor Referral Conversion Rate - {referralYear}
                    </a>
                </td>
                <td>
                    {nkrDonorReferralConversionRate.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.NkrDonorReferralConversionRateOverlay nkrDonorReferralConversionRate={nkrDonorReferralConversionRate} />
                        }>
                                <div style={{ cursor: 'pointer' }}>
                                    {nkrDonorReferralConversionRate.conversionRate >= 0 && (
                                        <>{nkrDonorReferralConversionRate.conversionRate}%</>
                                    )}

                                    {nkrDonorReferralConversionRate.conversionRate < 0 && (
                                        <>{nkrDonorReferralConversionRate.conversionRate}</>
                                    )}
                                </div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {nkrDonorReferralConversionRate.category != 'NV' ?
                        <div>
                            {getRankText(nkrDonorReferralConversionRate.rank)}
                        </div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderMissedMatchOfferDeadline = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let missedMatchOfferDeadline = props.data.missedMatchOfferDeadline;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/reports/my-center/missed-match-offers`} target='_blank'>
                        Missed Match Offer Deadline - Last 90 Days
                    </a>
                </td>
                <td>
                    {missedMatchOfferDeadline.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.MissedMatchOfferDeadlineOverlay missedMatchOfferDeadline={missedMatchOfferDeadline} />
                        }>
                            <div style={{ cursor: 'pointer' }}>
                                {Math.round(missedMatchOfferDeadline.ratio)}%
                            </div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {missedMatchOfferDeadline.category != 'NV' ?
                        <div>
                            {getRankText(missedMatchOfferDeadline.rank)}
                        </div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderSvDonations = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let svDonations = props.data.svDonations;
        const svDonationsPercent = svDonations.totalDonations > 0
            ? Math.round((svDonations.totalStandardVouchers / svDonations.totalDonations) * 100.0)
            : 0;
        const donatedStartDate = moment().subtract(12, 'month').format('YYYY-MM-DD');
        const donatedEndDate = moment().format('YYYY-MM-DD');
        const swapDonorsLink = `/center/${props.centerId}/donors/swap?center=${props.centerId}&status[]=donated&type[]=sv&donatedStartDate=${donatedStartDate}&donatedEndDate=${donatedEndDate}`;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={swapDonorsLink} target='_blank'>
                        Standard Voucher Donations - Last 12 Months
                    </a>
                </td>
                <td>
                    {svDonations.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.StandardVoucherDonationsOverlay svDonations={svDonations} />
                        }>
                            <div style={{ cursor: 'pointer' }}>{svDonationsPercent}%</div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {svDonations.category != 'NV' ?
                        <div>{getRankText(svDonations.rank)}</div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderFvDonations = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let fvDonations = props.data.fvDonations;
        const fvDonationsPercent = fvDonations.totalDonations > 0
            ? Math.round((fvDonations.totalFamilyVouchers / fvDonations.totalDonations) * 100.0)
            : 0;
        const donatedStartDate = moment().subtract(12, 'month').format('YYYY-MM-DD');
        const donatedEndDate = moment().format('YYYY-MM-DD');
        const swapDonorsLink = `/center/${props.centerId}/donors/swap?center=${props.centerId}&status[]=donated&type[]=fv&donatedStartDate=${donatedStartDate}&donatedEndDate=${donatedEndDate}`;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={swapDonorsLink} target='_blank'>
                        Family Voucher Donations - Last 12 Months
                    </a>
                </td>
                <td>
                    {fvDonations.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.FamilyVoucherDonationsOverlay fvDonations={fvDonations} />
                        }>
                            <div style={{ cursor: 'pointer' }}>{fvDonationsPercent}%</div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {fvDonations.category != 'NV' ?
                        <div>{getRankText(fvDonations.rank)}</div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderCombinedVoucherDisconnect = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let combinedVoucherDisconnect = props.data.combinedVoucherDisconnect;
        let referralYear = moment().month() < 4 ? (moment().year() - 1) : moment().year();
        let startDate = moment('01/01/' + referralYear, 'MM/DD/YYYY').format('YYYY-MM-DD');
        let endDate = moment('12/31/' + referralYear, 'MM/DD/YYYY').format('YYYY-MM-DD');
        let year = referralYear;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={`center/${props.centerId}/donors/donor-intake/search?id=0&adv=1&nkr=1&dash=1&ms=1&d=-1&s=2&stat=-1&organ=0&sd=${startDate}&ed=${endDate}`} target='_blank'>
                        Voucher Donor Disconnect Rate for NKR and Private Label DASH Referrals - {year}
                    </a>
                </td>
                <td>
                    {combinedVoucherDisconnect.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.VoucherDisconnectOverlay voucherDisconnect={combinedVoucherDisconnect} />
                        }>
                            <div style={{ cursor: 'pointer' }}>
                                {Math.round(combinedVoucherDisconnect.percentDisconnect)}%
                            </div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {combinedVoucherDisconnect.category != 'NV' ?
                        <div>
                            {getRankText(combinedVoucherDisconnect.rank)}
                        </div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderKflSuccessRate = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let kflSuccessRate = props.data.kflSuccessRate;
        const isKflEligible = kflSuccessRate.isParticipant;
        const kflValue = Math.round(kflSuccessRate.kidneyForLifeSuccessRate);
        const transplantedRecipientsLink = `/center/${props.centerId}/recipients/transplanted-recipients`;

        return (
            <>
                {isKflEligible && (
                    <tr>
                        <td>{rowNumber}</td>
                        <td>
                            <a href={transplantedRecipientsLink} target='_blank'>
                                Kidney for Life Success Rate - Last 12 Months
                            </a>
                        </td>
                        <td>
                            {kflSuccessRate.category != 'NV' ?
                                <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                                    <RTO.KflSuccessOverlay kflSuccessRate={kflSuccessRate} />
                                }>
                                    <div style={{ cursor: 'pointer' }}>
                                        
                                            {kflValue}%
                                    </div>
                                </OverlayTrigger>
                                :
                                <Label>{isKflEligible ? 'NV' : 'N/A'}</Label>
                            }
                        </td>
                        <td>
                            {isKflEligible && kflSuccessRate.category != 'NV' ?
                                <div>
                                    {getRankText(kflSuccessRate.rank)}
                                </div>
                                :
                                <Label>{isKflEligible ? 'NV' : 'N/A'}</Label>
                            }
                        </td>
                    </tr>
                )}
            </>
        );
    };

    const renderMicrositePatientReferral = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let micrositePatientReferralSuccessRate = props.data.micrositePatientReferralSuccessRate;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/action-center/referrals`} target='_blank'>
                        Microsite Patient Referral Success Rate
                    </a>
                </td>
                <td>
                    {micrositePatientReferralSuccessRate.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.MicrositePatientReferralSuccessRateOverlay micrositePatientReferralSuccessRate={micrositePatientReferralSuccessRate} />
                        }>
                            <div style={{ cursor: 'pointer' }}>
                                {Math.round(micrositePatientReferralSuccessRate.conversionRate)}%
                            </div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {micrositePatientReferralSuccessRate.category != 'NV' ?
                        <div>
                            {getRankText(micrositePatientReferralSuccessRate.rank)}
                        </div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderMicrositeEnrollmentRate = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let micrositeEnrollmentRate = props.data.micrositeEnrollmentRate;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/microsite/all`} target='_blank'>
                        Microsite Enrollment Success Rate
                    </a>
                </td>
                <td>
                    {micrositeEnrollmentRate.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.MicrositeEnrollmentRateOverlay micrositeEnrollmentRate={micrositeEnrollmentRate} />
                        }>
                            <div style={{ cursor: 'pointer' }}>
                                {Math.round(micrositeEnrollmentRate.micrositeEnrollmentRate)}%
                            </div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {micrositeEnrollmentRate.category != 'NV' ?
                        <div>
                            {getRankText(micrositeEnrollmentRate.rank)}
                        </div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderFiveStarSurvey = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let fiveStarSurveyResults = props.data.fiveStarSurveyResults;

        let startDate = moment().add(-1, 'year').format('YYYY-MM-DD');
        let endDate = moment().format('YYYY-MM-DD');

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/reports/dash-board/center-five-star-surveys?startDate=${startDate}&endDate=${endDate}`} target='_blank'>
                        Donor Satisfaction Survey Results - Last 12 Months
                    </a>
                </td>
                <td>
                    {fiveStarSurveyResults.category != 'NV' ?
                        <OverlayTrigger trigger={['hover', 'hover']} placement='left' overlay={
                            <RTO.FiveStarSurveysOverlay fiveStarSurveyResults={fiveStarSurveyResults} />
                        }>
                            <div style={{ cursor: 'pointer' }}>
                                {fiveStarSurveyResults.average.toFixed(1)}
                            </div>
                        </OverlayTrigger>
                        :
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {fiveStarSurveyResults.category != 'NV' ?
                        <div>
                            {getRankText(fiveStarSurveyResults.rank)}
                        </div>
                        :
                        <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderCprScore = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let cprScore = props.data.cprScore;
        let yesterday = moment().subtract(1, 'days').format('YYYY-MM-DD');

        return (
            <tr>
                <td>{rowNumber}</td>
                <td>
                    <a href={`/center/${props.centerId}/reports/my-center/cpr-score-audit-report?endDate=${yesterday}`} target='_blank'>
                        Compatible Pair Ratio - Last 6 Months
                    </a>
                </td>
                <td>
                    {cprScore.category != 'NV' ? 
                        <OverlayTrigger trigger={['hover', 'hover']} placement={'left'} overlay={
                            <RTO.CompatiblePairRatioOverlay />
                        }>
                            <div style={{cursor: 'pointger'}}>
                                {(cprScore.cprScore * 100.0).toFixed(1)}%
                            </div>
                        </OverlayTrigger>
                        : 
                        <Label>NV</Label>
                    }
                </td>
                <td>
                    {cprScore.category != 'NV'
                        ? getRankText(cprScore.rank)
                        : <Label>NV</Label>
                    }
                </td>
            </tr>
        );
    };

    const renderOverallPerformance = (rowNumber: number) => {
        if (props.data == null)
            return null;

        let overallPerformance = props.data.overallPerformance;

        return (
            <tr>
                <td>{rowNumber}</td>
                <td style={{ fontWeight: 700 }}>
                    <a href={`/center/${props.centerId}/reports/all-centers/overall-performance`} target='_blank'>
                        Overall Performance
                    </a>
                </td>
                <td></td>
                <td>{getRankText(overallPerformance.rank)} out of {props.memberCenterCount}</td>
            </tr>
        );
    };

    let rowNumber = 1;

    let rows = [
        renderCompositeScore,
        render30DayPreselect,
        renderCprScore,
        renderSurgeryDateDecline,
        renderSwapFailureRate,
        renderChainEndDeclineRatio,
        renderNkrDonorReferralMedianContactTime,
        renderNkrDonorReferralConversionRate,
        renderSvDonations,
        renderFvDonations,
        renderCombinedVoucherDisconnect,
        renderMissedMatchOfferDeadline,
        renderKflSuccessRate,
        renderMicrositeEnrollmentRate,
        renderFiveStarSurvey,
        renderOverallPerformance
    ];

    if (props.data == null) {
        return (
            <Panel>
                <Panel.Heading>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Panel.Title>Relative Performance</Panel.Title>
                        <div>Hover over value for details...</div>
                    </div>
                </Panel.Heading>
                <Panel.Body><Label style={{ fontSize: '100%' }}>Loading data, one moment please...</Label></Panel.Body>
            </Panel>
        );
    }

    return (
        <>
            <Panel>
                <Panel.Heading>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Panel.Title>Relative Performance</Panel.Title>
                        <div>Hover over value for details...</div>
                    </div>
                </Panel.Heading>

                {props.loading ?
                    <Panel.Body><Label style={{ fontSize: '100%' }}>Loading data, one moment please...</Label></Panel.Body>
                    :
                    <TableWrapper condensed bordered>
                        <thead>
                            <tr>
                                <th colSpan={2}></th>
                                <th style={{ width: '12%' }}>Value</th>
                                <th style={{ width: '12%' }}>Rank in Network</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                if (row.name == "renderKflSuccessRate" && !props.data.kflSuccessRate.isParticipant) {
                                    return null;
                                } else if (row.name == "renderMicrositeEnrollmentRate" && !props.data.micrositeEnrollmentRate.isParticipant){
                                    return null;
                                }
                                
                                return row(rowNumber++);
                            })}
                        </tbody>
                    </TableWrapper>
                }

            </Panel>
        </>
    );
}

export default RelativePerformance;
